import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@material-tailwind/react";
import { FiSearch } from "react-icons/fi";
import { RiFilter3Line, RiMedicineBottleLine, RiBuilding4Line, RiLeafLine } from "react-icons/ri";
import SearchSuggestions from "./SearchSuggestions";
// Image search feature hidden - paused due to development cost
// import { useImageSearch } from "../hooks/useImageSearch";

export default function SearchBar({
  initialQuery = "",
  initialType = "all",
  className = "",
  placeholder = ""
}) {
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState(initialQuery);
  const [searchType, setSearchType] = useState(initialType);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const searchTypeOptions = [
    { value: "all", label: "All", icon: RiFilter3Line, color: "bg-secondary" },
    { value: "tradenames", label: "Brand names", icon: RiMedicineBottleLine, color: "bg-primary" },
    { value: "manufacturers", label: "Manufacturers", icon: RiBuilding4Line, color: "bg-purple-500" },
    { value: "ingredients", label: "Ingredients", icon: RiLeafLine, color: "bg-emerald-600" }
  ];

  const onInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    setShowSuggestions(value.length >= 2);
  };

  const handleSearch = (e) => {
    setShowSuggestions(false);
    if (inputValue.trim()) {
      navigate(`/search?q=${encodeURIComponent(inputValue)}&type=${searchType}&page=1`);
    } else {
      navigate(`/search?q=&type=${searchType}&page=1`);
    }
  };

  const handleSearchTypeChange = (newType) => {
    setSearchType(newType);
  };

  const handleSuggestionSelect = useCallback((value) => {
    setInputValue(value);
    setShowSuggestions(false);
  }, []);

  const handleSuggestionsClose = useCallback(() => {
    setShowSuggestions(false);
  }, []);

  const handleFocus = () => {
    if (inputValue.length >= 2) {
      setShowSuggestions(true);
    }
  };

  const getPlaceholder = () => {
    if (placeholder) return placeholder;

    switch (searchType) {
      case "tradenames":
        return "Search by trade name...";
      case "manufacturers":
        return "Search by manufacturer...";
      case "ingredients":
        return "Search by active ingredient...";
      default:
        return "Search by name or ingredient...";
    }
  };

  return (
    <div className={`max-w-[800px] relative ${className}`}>
      {/* Type filter pills - always visible with text labels */}
      <div className="flex items-center gap-2 mb-3 flex-wrap" role="radiogroup" aria-label="Search category">
        {searchTypeOptions.map((option) => {
          const Icon = option.icon;
          const isActive = searchType === option.value;
          return (
            <button
              key={option.value}
              onClick={() => handleSearchTypeChange(option.value)}
              role="radio"
              aria-checked={isActive}
              className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-14 font-text font-medium transition-colors min-h-[44px] ${
                isActive
                  ? `${option.color} text-white shadow-sm`
                  : "bg-white text-secondary/80 border border-secondary/15 hover:border-secondary/30 hover:bg-white/80"
              }`}
            >
              <Icon className="size-4" />
              {option.label}
            </button>
          );
        })}
      </div>

      {/* Main search container */}
      <div className="relative flex items-center rounded-full bg-white/60 border border-white/80 transition-all duration-200">
        {/* Text input */}
        <Input
          type="text"
          placeholder={getPlaceholder()}
          defaultValue={inputValue}
          onChange={onInputChange}
          onFocus={handleFocus}
          onKeyDown={e => {
            if (e.key === "Enter") {
              handleSearch(e);
            }
            if (e.key === "Escape") {
              setShowSuggestions(false);
            }
          }}
          className="w-full rounded-full text-16 shadow-none border-none pl-5 !pr-16 h-14 text-secondary focus:border-none focus:ring-0 focus:shadow-none hover:shadow-none hover:ring-0 ring-0"
          containerProps={{
            className: "min-w-0 flex-grow",
          }}
        />

        {/* Search button - positioned absolutely to avoid Input.Icon click issues */}
        <button
          type="button"
          onClick={handleSearch}
          className="absolute right-1.5 flex items-center justify-center w-11 h-11 rounded-full cursor-pointer bg-primary hover:bg-primary/90 active:scale-95 text-white shadow-sm transition-all"
          aria-label="Search"
        >
          <FiSearch className="size-5" />
        </button>
      </div>

      {/* Keyword suggestions dropdown */}
      <SearchSuggestions
        query={inputValue}
        isVisible={showSuggestions}
        onSelect={handleSuggestionSelect}
        onClose={handleSuggestionsClose}
        searchType={searchType}
      />
    </div>
  );
}

// Image search feature hidden - kept for future re-enablement
// See src/hooks/useImageSearch.js for the full implementation
